import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import {
  allSearchDepths,
  allTermRequirements,
  allTermTargets,
  CareAIChatKnowledgeSource,
  CareAIChatPreferences,
  createNewPreference,
  SearchDepth,
  TermFilter,
  TermRequirment,
  TermTarget,
} from '../../care-ai-chat.model';

@Component({
  selector: 'carehub-care-ai-preferences',
  standalone: true,
  imports: [
    ButtonModule,
    CheckboxModule,
    ChipModule,
    DatePickerModule,
    InputTextModule,
    MultiSelectModule,
    ReactiveFormsModule,
    SelectModule,
    ToggleSwitchModule,
  ],
  templateUrl: './care-ai-preferences.component.html',
  styleUrl: './care-ai-preferences.component.scss',
})
export class CareAiPreferencesComponent implements OnChanges {
  @Input() preferences?: CareAIChatPreferences;
  @Input({ required: true }) sources: CareAIChatKnowledgeSource[] = [];
  @Output() cancelPreferences = new EventEmitter<void>();
  @Output() savePreferences = new EventEmitter<CareAIChatPreferences>();

  preferenceForm: FormGroup;
  newIncludeTermForm: FormGroup;
  newExcludeTermForm: FormGroup;

  constructor(private _formBuilder: FormBuilder) {
    this.preferenceForm = this._formBuilder.group({
      sourceTypes: [],
      fromLastModified: null,
      includedTermsFilter: this._formBuilder.group({
        terms: [],
        targetFields: [],
        termRequirement: 0,
      }),
      excludedTermsFilter: this._formBuilder.group({
        terms: [],
        targetFields: [],
        termRequirement: 0,
      }),
      searchDepth: 1,
      collapseMatches: true,
    });

    this.newIncludeTermForm = this._formBuilder.group({ term: ['', Validators.required] });
    this.newExcludeTermForm = this._formBuilder.group({ term: ['', Validators.required] });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['preferences']) {
      return;
    }

    this.preferenceForm.reset({
      ...createNewPreference(),
      collapseMatches: true,
      ...this.preferences,
    });

    this.newIncludeTermForm.reset({ term: '' });
    this.newExcludeTermForm.reset({ term: '' });
    this.preferenceForm.markAsPristine();
  }

  addTerm(termForm: FormGroup, type: 'included' | 'excluded'): void {
    if (!termForm.valid || termForm.pristine) {
      return;
    }

    const newTerm: string = termForm.get('term')?.value?.trim();
    if (newTerm === '') {
      return;
    }

    const filter: TermFilter = this.preferenceForm.get(`${type}TermsFilter`)?.value;
    if (!filter) {
      return;
    }

    this.preferenceForm.get(`${type}TermsFilter`)?.setValue({
      ...filter,
      terms: [...filter.terms, newTerm],
    });
    termForm.reset({ term: '' });
    termForm.markAsPristine();
  }

  removeTerm(removeTerm: string, type: 'included' | 'excluded'): void {
    const filter: TermFilter | null = this.preferenceForm.get(`${type}TermsFilter`)?.value;
    if (!filter) {
      return;
    }

    this.preferenceForm.get(`${type}TermsFilter`)?.setValue({
      ...filter,
      terms: filter.terms.filter(term => term !== removeTerm),
    });
  }

  save(): void {
    if (!this.preferenceForm.valid) {
      return;
    }

    this.savePreferences.emit(this.preferenceForm.getRawValue() as CareAIChatPreferences);
  }

  get allTermTargets(): TermTarget[] {
    return allTermTargets;
  }

  get allTermRequirements(): TermRequirment[] {
    return allTermRequirements;
  }

  get allSearchDepths(): SearchDepth[] {
    return allSearchDepths;
  }

  get includedFilterTerms(): string[] {
    const filter: TermFilter = this.preferenceForm.get('includedTermsFilter')?.value;
    return filter?.terms ?? [];
  }

  get excludedFilterTerms(): string[] {
    const filter: TermFilter = this.preferenceForm.get('excludedTermsFilter')?.value;
    return filter?.terms ?? [];
  }
}
